import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import api from '../../services/api';
import {
  Search,
  Eye,
  ShoppingCart,
  Calendar,
  X,
  Package,
  DollarSign,
  ChevronLeft,
  ChevronRight,
  Download,
  FileText,
  FileSpreadsheet
} from 'lucide-react';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';

const AdminOrders = () => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({ page: 1, pages: 1, total: 0 });
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [paymentFilter, setPaymentFilter] = useState('');

  useEffect(() => {
    fetchOrders();
  }, [pagination.page, searchQuery, statusFilter, paymentFilter]);

  const fetchOrders = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams({
        page: pagination.page,
        limit: 10,
        ...(searchQuery && { search: searchQuery }),
        ...(statusFilter && { status: statusFilter }),
        ...(paymentFilter && { paymentStatus: paymentFilter })
      });
      const response = await api.get(`/admin/orders?${params}`);
      setOrders(response.data.data.orders);
      setPagination(response.data.data.pagination);
    } catch (err) {
      console.error('Failed to load orders:', err);
    } finally {
      setLoading(false);
    }
  };

  const clearFilters = () => {
    setSearchQuery('');
    setStatusFilter('');
    setPaymentFilter('');
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  const hasActiveFilters = searchQuery || statusFilter || paymentFilter;

  const [showDownloadMenu, setShowDownloadMenu] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const downloadMenuRef = useRef(null);

  // Close download menu on outside click
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (downloadMenuRef.current && !downloadMenuRef.current.contains(e.target)) {
        setShowDownloadMenu(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const fetchExportData = async () => {
    const params = new URLSearchParams();
    if (statusFilter) params.set('status', statusFilter);
    if (paymentFilter) params.set('paymentStatus', paymentFilter);
    if (searchQuery) params.set('search', searchQuery);
    const queryStr = params.toString();
    const res = await api.get(`/admin/orders/export${queryStr ? '?' + queryStr : ''}`);
    return res.data.data;
  };

  const downloadAsPDF = async () => {
    setIsExporting(true);
    setShowDownloadMenu(false);
    try {
      const exportData = await fetchExportData();
      const doc = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a4' });
      const pageWidth = doc.internal.pageSize.getWidth();

      // Title
      doc.setFontSize(20);
      doc.setTextColor(40, 40, 40);
      doc.text('Beetle Diffuser - Orders Report', 14, 16);
      doc.setFontSize(9);
      doc.setTextColor(120, 120, 120);
      doc.text(`Exported: ${new Date().toLocaleString()}  |  Total Orders: ${exportData.total}`, 14, 23);
      doc.setDrawColor(234, 88, 12);
      doc.setLineWidth(0.5);
      doc.line(14, 25, pageWidth - 14, 25);

      // --- ORDERS SUMMARY TABLE ---
      doc.setFontSize(13);
      doc.setTextColor(40, 40, 40);
      doc.text('Orders Summary', 14, 33);

      const summaryRows = exportData.orders.map((o) => {
        const sym = (o.currency === 'USD') ? '$' : '\u20B9';
        return [
          o.orderNumber,
          o.customerName,
          o.customerPhone || '-',
          o.itemsSummary || '-',
          `${sym}${Number(o.totalAmount).toLocaleString()}`,
          (o.status || '').charAt(0).toUpperCase() + (o.status || '').slice(1),
          (o.paymentStatus || '').charAt(0).toUpperCase() + (o.paymentStatus || '').slice(1),
          o.paymentMethod || '-',
          o.shippingFullAddress || '-',
          o.trackingNumber || '-',
          o.orderDate ? new Date(o.orderDate).toLocaleDateString() : '-'
        ];
      });

      autoTable(doc, {
        startY: 36,
        head: [['Order #', 'Customer', 'Phone', 'Items', 'Total', 'Status', 'Payment', 'Method', 'Ship To', 'Tracking', 'Date']],
        body: summaryRows,
        styles: { fontSize: 6.5, cellPadding: 1.5 },
        headStyles: { fillColor: [234, 88, 12], textColor: 255, fontStyle: 'bold', fontSize: 7 },
        alternateRowStyles: { fillColor: [255, 247, 237] },
        columnStyles: {
          0: { cellWidth: 18 }, 1: { cellWidth: 25 }, 2: { cellWidth: 22 },
          3: { cellWidth: 40 }, 4: { cellWidth: 18 }, 5: { cellWidth: 18 },
          6: { cellWidth: 16 }, 7: { cellWidth: 16 }, 8: { cellWidth: 45 },
          9: { cellWidth: 18 }, 10: { cellWidth: 18 }
        },
        margin: { left: 8, right: 8 },
        theme: 'grid',
      });

      // --- DETAILED ORDER SECTION ---
      let currentY = doc.lastAutoTable.finalY + 12;

      exportData.orders.forEach((order, idx) => {
        const sym = (order.currency === 'USD') ? '$' : '\u20B9';

        // Check if we need a new page
        if (currentY > 170) {
          doc.addPage();
          currentY = 15;
        }

        // Order header
        doc.setFillColor(234, 88, 12);
        doc.rect(8, currentY - 4, pageWidth - 16, 8, 'F');
        doc.setFontSize(10);
        doc.setTextColor(255, 255, 255);
        doc.text(`Order: ${order.orderNumber}  |  ${order.customerName}  |  ${order.orderDate ? new Date(order.orderDate).toLocaleString() : ''}`, 12, currentY + 1);
        currentY += 8;

        // Customer & Address info
        doc.setFontSize(8);
        doc.setTextColor(60, 60, 60);
        const infoLines = [
          `Customer: ${order.customerName} (${order.customerType})  |  Email: ${order.customerEmail}  |  Phone: ${order.customerPhone}`,
          `Shipping: ${order.shippingFullAddress || 'N/A'}  |  Phone: ${order.shippingPhone || '-'}`,
          `Status: ${order.status}  |  Payment: ${order.paymentStatus} (${order.paymentMethod || '-'})  |  Payment ID: ${order.paymentId || '-'}  |  Tracking: ${order.trackingNumber || '-'}`,
          `Coupon: ${order.couponCode || 'None'}  |  Region: ${order.region || '-'}  |  Currency: ${order.currency || 'INR'}`
        ];
        infoLines.forEach(line => {
          doc.text(line, 12, currentY);
          currentY += 4;
        });
        if (order.notes) {
          doc.text(`Notes: ${order.notes}`, 12, currentY);
          currentY += 4;
        }
        currentY += 1;

        // Items table
        const itemRows = order.items.map((item, i) => [
          i + 1,
          item.name,
          item.sku || '-',
          item.variant || '-',
          item.size || '-',
          item.cameraModel || '-',
          item.lensModel || '-',
          item.flashModel || '-',
          item.quantity,
          `${sym}${Number(item.price).toLocaleString()}`,
          `${sym}${Number(item.lineTotal).toLocaleString()}`
        ]);

        autoTable(doc, {
          startY: currentY,
          head: [['#', 'Product', 'SKU', 'Variant', 'Size', 'Camera', 'Lens', 'Flash', 'Qty', 'Price', 'Line Total']],
          body: itemRows,
          styles: { fontSize: 6.5, cellPadding: 1.2 },
          headStyles: { fillColor: [80, 80, 80], textColor: 255, fontStyle: 'bold', fontSize: 6.5 },
          alternateRowStyles: { fillColor: [248, 248, 248] },
          margin: { left: 10, right: 10 },
          theme: 'grid',
        });

        currentY = doc.lastAutoTable.finalY + 2;

        // Pricing summary
        doc.setFontSize(7);
        doc.setTextColor(80, 80, 80);
        const priceX = pageWidth - 70;
        const pricingLines = [
          [`Subtotal:`, `${sym}${Number(order.subtotal).toLocaleString()}`],
          [`Tax:`, `${sym}${Number(order.tax).toLocaleString()}`],
          [`Shipping:`, `${sym}${Number(order.shipping).toLocaleString()}`],
          [`Discount:`, `-${sym}${Number(order.discount).toLocaleString()}`],
        ];
        pricingLines.forEach(([label, val]) => {
          doc.text(label, priceX, currentY);
          doc.text(val, priceX + 35, currentY);
          currentY += 3.5;
        });
        doc.setFontSize(8);
        doc.setTextColor(234, 88, 12);
        doc.text('TOTAL:', priceX, currentY);
        doc.text(`${sym}${Number(order.totalAmount).toLocaleString()}`, priceX + 35, currentY);
        currentY += 10;
      });

      doc.save(`orders_detailed_report_${new Date().toISOString().slice(0,10)}.pdf`);
      alert(`Exported ${exportData.total} orders as PDF`);
    } catch (err) {
      console.error('PDF export error:', err);
      alert('Failed to export PDF: ' + (err.message || err));
    } finally {
      setIsExporting(false);
    }
  };

  const downloadAsExcel = async () => {
    setIsExporting(true);
    setShowDownloadMenu(false);
    try {
      const exportData = await fetchExportData();

      // Sheet 1: Orders Summary (one row per order)
      const ordersData = exportData.orders.map((o) => ({
        'Order #': o.orderNumber,
        'Order Date': o.orderDate ? new Date(o.orderDate).toLocaleString() : '',
        'Last Updated': o.lastUpdated ? new Date(o.lastUpdated).toLocaleString() : '',
        'Customer Type': o.customerType,
        'Customer Name': o.customerName,
        'Email': o.customerEmail,
        'Phone': o.customerPhone,
        'Items Summary': o.itemsSummary,
        'Total Items': o.totalItems,
        'Currency': o.currency || 'INR',
        'Subtotal': Number(o.subtotal).toFixed(2),
        'Tax': Number(o.tax).toFixed(2),
        'Shipping Fee': Number(o.shipping).toFixed(2),
        'Discount': Number(o.discount).toFixed(2),
        'Total Amount': Number(o.totalAmount).toFixed(2),
        'Coupon Code': o.couponCode || '',
        'Region': o.region || '',
        'Order Status': o.status,
        'Payment Status': o.paymentStatus,
        'Payment Method': o.paymentMethod || '',
        'Payment ID': o.paymentId || '',
        'Razorpay Order ID': o.razorpayOrderId || '',
        'Tracking Number': o.trackingNumber || '',
        'Ship To - Name': [o.shippingFirstName, o.shippingLastName].filter(Boolean).join(' '),
        'Ship To - Street': o.shippingStreet,
        'Ship To - City': o.shippingCity,
        'Ship To - State': o.shippingState,
        'Ship To - Zip': o.shippingZipCode,
        'Ship To - Country': o.shippingCountry,
        'Ship To - Phone': o.shippingPhone,
        'Ship To - Full Address': o.shippingFullAddress,
        'Notes': o.notes || '',
      }));

      // Sheet 2: Order Items (one row per item, linked by order number)
      const itemsData = [];
      exportData.orders.forEach(o => {
        (o.items || []).forEach((item, idx) => {
          itemsData.push({
            'Order #': o.orderNumber,
            'Order Date': o.orderDate ? new Date(o.orderDate).toLocaleString() : '',
            'Customer': o.customerName,
            'Item #': idx + 1,
            'Product Name': item.name,
            'SKU': item.sku || '',
            'Variant': item.variant || '',
            'Size': item.size || '',
            'Camera Model': item.cameraModel || '',
            'Lens Model': item.lensModel || '',
            'Flash Model': item.flashModel || '',
            'Quantity': item.quantity,
            'Unit Price': Number(item.price).toFixed(2),
            'Line Total': Number(item.lineTotal).toFixed(2),
            'Currency': o.currency || 'INR',
            'Product ID': item.productId || '',
          });
        });
      });

      // Auto-width function
      const autoWidth = (data) => {
        if (!data.length) return [];
        return Object.keys(data[0]).map(key => ({
          wch: Math.max(key.length, ...data.map(r => String(r[key] || '').length).slice(0, 100)) + 2
        }));
      };

      const wb = XLSX.utils.book_new();

      const ws1 = XLSX.utils.json_to_sheet(ordersData);
      ws1['!cols'] = autoWidth(ordersData);
      XLSX.utils.book_append_sheet(wb, ws1, 'Orders');

      if (itemsData.length > 0) {
        const ws2 = XLSX.utils.json_to_sheet(itemsData);
        ws2['!cols'] = autoWidth(itemsData);
        XLSX.utils.book_append_sheet(wb, ws2, 'Order Items');
      }

      const buf = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
      saveAs(new Blob([buf], { type: 'application/octet-stream' }), `orders_detailed_report_${new Date().toISOString().slice(0,10)}.xlsx`);
      alert(`Exported ${exportData.total} orders as Excel (2 sheets)`);
    } catch (err) {
      console.error('Excel export error:', err);
      alert('Failed to export Excel: ' + (err.message || err));
    } finally {
      setIsExporting(false);
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      pending: 'bg-yellow-100 text-yellow-800',
      confirmed: 'bg-blue-100 text-blue-800',
      processing: 'bg-indigo-100 text-indigo-800',
      shipped: 'bg-cyan-100 text-cyan-800',
      delivered: 'bg-green-100 text-green-800',
      cancelled: 'bg-red-100 text-red-800',
      refunded: 'bg-gray-100 text-gray-800'
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  const getPaymentColor = (status) => {
    const colors = {
      pending: 'bg-yellow-100 text-yellow-800',
      paid: 'bg-green-100 text-green-800',
      failed: 'bg-red-100 text-red-800',
      refunded: 'bg-gray-100 text-gray-800'
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  const statusOptions = ['pending', 'confirmed', 'processing', 'shipped', 'delivered', 'cancelled'];
  const paymentOptions = ['pending', 'paid', 'failed', 'refunded'];

  const formatCurrency = (amount, currency = 'INR') => {
    const currencySymbol = currency === 'USD' ? '$' : '₹';
    const locale = currency === 'USD' ? 'en-US' : 'en-IN';
    return `${currencySymbol}${amount?.toLocaleString(locale) || 0}`;
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Orders</h1>
          <p className="mt-1 text-gray-500">Manage customer orders and track fulfillment</p>
        </div>
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2 px-3 py-2 bg-white border border-gray-200 rounded-xl">
            <Package className="h-4 w-4 text-orange-500" />
            <span className="text-sm text-gray-600">{pagination.total || 0} total orders</span>
          </div>

          {/* Download Button */}
          <div className="relative" ref={downloadMenuRef}>
            <button
              onClick={() => setShowDownloadMenu(!showDownloadMenu)}
              disabled={isExporting}
              className={`flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-medium transition-all border ${
                isExporting
                  ? 'opacity-60 cursor-not-allowed bg-gray-100 border-gray-200 text-gray-400'
                  : 'bg-orange-50 text-orange-600 border-orange-200 hover:bg-orange-100'
              }`}
            >
              {isExporting ? (
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-orange-500" />
              ) : (
                <Download className="h-4 w-4" />
              )}
              {isExporting ? 'Exporting...' : 'Export'}
            </button>

            {showDownloadMenu && (
              <div className="absolute right-0 mt-2 w-52 bg-white rounded-xl shadow-xl border border-gray-200 z-50 overflow-hidden">
                <button
                  onClick={downloadAsPDF}
                  className="flex items-center gap-3 w-full px-4 py-3 text-sm text-left text-gray-700 hover:bg-gray-50 hover:text-gray-900 transition-colors"
                >
                  <FileText className="h-4 w-4 text-red-400" />
                  <div>
                    <div className="font-medium">Download PDF</div>
                    <div className="text-xs text-gray-400">Print-ready report</div>
                  </div>
                </button>
                <div className="border-t border-gray-100" />
                <button
                  onClick={downloadAsExcel}
                  className="flex items-center gap-3 w-full px-4 py-3 text-sm text-left text-gray-700 hover:bg-gray-50 hover:text-gray-900 transition-colors"
                >
                  <FileSpreadsheet className="h-4 w-4 text-green-400" />
                  <div>
                    <div className="font-medium">Download Excel</div>
                    <div className="text-xs text-gray-400">Spreadsheet backup</div>
                  </div>
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Status Filter Pills */}
      <div className="flex flex-wrap gap-2">
        <button
          onClick={() => { setStatusFilter(''); setPagination(prev => ({ ...prev, page: 1 })); }}
          className={`px-4 py-2 rounded-xl text-sm font-medium transition-all ${
            statusFilter === ''
              ? 'bg-orange-100 text-orange-700 border border-orange-300'
              : 'bg-white text-gray-500 border border-gray-200 hover:bg-gray-50'
          }`}
        >
          All Status
        </button>
        {statusOptions.map(status => (
          <button
            key={status}
            onClick={() => { 
              setStatusFilter(status === statusFilter ? '' : status); 
              setPagination(prev => ({ ...prev, page: 1 })); 
            }}
            className={`px-4 py-2 rounded-xl text-sm font-medium transition-all capitalize ${
              statusFilter === status
                ? 'bg-orange-100 text-orange-700 border border-orange-300'
                : 'bg-white text-gray-500 border border-gray-200 hover:bg-gray-50'
            }`}
          >
            {status}
          </button>
        ))}
      </div>

      {/* Search & Filters */}
      <div className="bg-white rounded-2xl p-4 shadow-sm border border-gray-100">
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="flex-1 relative">
            <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
            <input
              type="text"
              placeholder="Search by order number, customer name or email..."
              className="w-full px-4 py-3 pl-11 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
              value={searchQuery}
              onChange={(e) => {
                setSearchQuery(e.target.value);
                setPagination(prev => ({ ...prev, page: 1 }));
              }}
            />
          </div>
          
          <select
            className="px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-orange-500"
            value={paymentFilter}
            onChange={(e) => {
              setPaymentFilter(e.target.value);
              setPagination(prev => ({ ...prev, page: 1 }));
            }}
          >
            <option value="">All Payments</option>
            {paymentOptions.map(status => (
              <option key={status} value={status} className="capitalize">{status}</option>
            ))}
          </select>
        </div>

        {/* Active Filters */}
        {hasActiveFilters && (
          <div className="flex flex-wrap items-center gap-2 mt-4 pt-4 border-t border-gray-100">
            <span className="text-sm text-gray-500">Active filters:</span>
            {searchQuery && (
              <span className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-orange-100 text-orange-700 rounded-lg text-sm">
                Search: {searchQuery}
                <button onClick={() => setSearchQuery('')} className="hover:text-orange-900">
                  <X className="h-3.5 w-3.5" />
                </button>
              </span>
            )}
            {statusFilter && (
              <span className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-blue-100 text-blue-700 rounded-lg text-sm capitalize">
                Status: {statusFilter}
                <button onClick={() => setStatusFilter('')} className="hover:text-blue-900">
                  <X className="h-3.5 w-3.5" />
                </button>
              </span>
            )}
            {paymentFilter && (
              <span className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-green-100 text-green-700 rounded-lg text-sm capitalize">
                Payment: {paymentFilter}
                <button onClick={() => setPaymentFilter('')} className="hover:text-green-900">
                  <X className="h-3.5 w-3.5" />
                </button>
              </span>
            )}
            <button onClick={clearFilters} className="text-sm text-gray-500 hover:text-gray-700">
              Clear all
            </button>
          </div>
        )}
      </div>

      {/* Orders Table */}
      <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center h-64">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-500"></div>
          </div>
        ) : orders.length === 0 ? (
          <div className="text-center py-16">
            <ShoppingCart className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-gray-900 mb-2">No orders found</h3>
            <p className="text-gray-500 mb-6">
              {hasActiveFilters 
                ? 'Try adjusting your filters to find what you\'re looking for.'
                : 'Orders will appear here when customers make purchases.'}
            </p>
            {hasActiveFilters && (
              <button onClick={clearFilters} className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50">
                Clear Filters
              </button>
            )}
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-4 text-left text-xs font-semibold uppercase tracking-wider text-gray-500">Order</th>
                    <th className="px-6 py-4 text-left text-xs font-semibold uppercase tracking-wider text-gray-500">Customer</th>
                    <th className="px-6 py-4 text-left text-xs font-semibold uppercase tracking-wider text-gray-500">Date</th>
                    <th className="px-6 py-4 text-left text-xs font-semibold uppercase tracking-wider text-gray-500">Items</th>
                    <th className="px-6 py-4 text-left text-xs font-semibold uppercase tracking-wider text-gray-500">Total</th>
                    <th className="px-6 py-4 text-left text-xs font-semibold uppercase tracking-wider text-gray-500">Status</th>
                    <th className="px-6 py-4 text-left text-xs font-semibold uppercase tracking-wider text-gray-500">Payment</th>
                    <th className="px-6 py-4 text-left text-xs font-semibold uppercase tracking-wider text-gray-500">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {orders.map((order) => (
                    <tr key={order._id} className="hover:bg-gray-50 transition-colors">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 bg-orange-100 rounded-lg flex items-center justify-center">
                            <ShoppingCart className="h-5 w-5 text-orange-600" />
                          </div>
                          <span className="text-sm font-medium text-gray-900">
                            #{order.orderNumber}
                          </span>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div>
                          <div className="text-sm font-medium text-gray-900">
                            {order.user?.name || order.guestInfo?.firstName || 'N/A'}
                          </div>
                          <div className="text-sm text-gray-500">
                            {order.user?.email || order.guestInfo?.email || '-'}
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center gap-2 text-sm text-gray-600">
                          <Calendar className="h-4 w-4 text-gray-400" />
                          {new Date(order.createdAt).toLocaleDateString('en-IN', {
                            month: 'short',
                            day: 'numeric',
                            year: 'numeric'
                          })}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className="px-2 py-1 rounded-lg bg-gray-100 text-sm text-gray-700">
                          {order.items?.length || 0} item{order.items?.length !== 1 ? 's' : ''}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-semibold flex items-center gap-1 text-gray-900">
                          <DollarSign className="h-4 w-4 text-green-500" />
                          {formatCurrency(order.totalAmount, order.currency)}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`px-3 py-1 rounded-full text-xs font-medium capitalize ${getStatusColor(order.status)}`}>
                          {order.status}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`px-3 py-1 rounded-full text-xs font-medium capitalize ${getPaymentColor(order.paymentStatus)}`}>
                          {order.paymentStatus}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <Link
                          to={`/admin/orders/${order._id}`}
                          className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg bg-gray-100 hover:bg-orange-50 text-gray-600 hover:text-orange-600 transition-all text-sm"
                        >
                          <Eye className="h-4 w-4" />
                          View
                        </Link>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            
            {/* Pagination */}
            <div className="border-t border-gray-100 px-6 py-4 flex items-center justify-between">
              <p className="text-sm text-gray-500">
                {pagination.total > 0 
                  ? `Showing ${((pagination.page - 1) * 10) + 1} to ${Math.min(pagination.page * 10, pagination.total)} of ${pagination.total} orders`
                  : 'No orders found'}
              </p>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setPagination(prev => ({ ...prev, page: prev.page - 1 }))}
                  disabled={pagination.page === 1}
                  className="p-2 rounded-lg border border-gray-300 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                >
                  <ChevronLeft className="h-4 w-4" />
                </button>
                <span className="px-4 py-2 text-sm font-medium">
                  Page {pagination.page} of {pagination.pages || 1}
                </span>
                <button
                  onClick={() => setPagination(prev => ({ ...prev, page: prev.page + 1 }))}
                  disabled={pagination.page >= pagination.pages}
                  className="p-2 rounded-lg border border-gray-300 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                >
                  <ChevronRight className="h-4 w-4" />
                </button>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default AdminOrders;
